import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Creatable } from "react-select";
import { Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { firestore } from '../../helpers/Firebase';
import { filterSortedObject, getAllOptions, sortObjectByCategory } from '../../helpers/Utils';
import CustomSelectInput from './CustomSelectInput';

const ModifiersSelect = ({
    selectedModifiers = [],
    onChange,
    onCreateModifier,
    disabled = false,
    placeholder = "Select modifiers...",
    className = "react-select",
    isMulti = true
}) => {
    const { modifiers } = useSelector(state => state.providers);
    const [newModifierModal, setNewModifierModal] = useState(false);
    const [newModifier, setNewModifier] = useState({ name: '', name_ar: '' });
    const [loading, setLoading] = useState(false);

    // Sort modifiers by category for grouped display
    const sortedModifiers = sortObjectByCategory(modifiers || {});

    // Group styles for react-select
    const groupStyles = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    };

    const groupBadgeStyles = {
        backgroundColor: '#EBECF0',
        borderRadius: '2em',
        color: '#172B4D',
        display: 'inline-block',
        fontSize: 12,
        fontWeight: 'normal',
        lineHeight: '1',
        minWidth: 1,
        padding: '0.16666666666667em 0.5em',
        textAlign: 'center',
    };

    const formatGroupLabel = (data) => (
        <div style={groupStyles}>
            <span>{data.label}</span>
            <span style={groupBadgeStyles}>{data.options.length}</span>
        </div>
    );

    // Handle selection change
    const handleChange = (selectedOption) => {
        if (isMulti) {
            const newSelectedIds = selectedOption ? selectedOption.map(option => option.key) : [];
            onChange && onChange(newSelectedIds);
        } else {
            onChange && onChange(selectedOption ? selectedOption.key : null);
        }
    };

    // Handle creating new modifier
    const handleCreateOption = (inputValue) => {
        setNewModifier({ name: inputValue, name_ar: '' });
        setNewModifierModal(true);
    };

    // Save new modifier to Firebase
    const saveNewModifier = async () => {
        if (!newModifier.name || !newModifier.name_ar) return;

        setLoading(true);
        try {
            const id = firestore().collection('modifiers').doc().id;
            const modifierData = {
                ...newModifier,
                key: id,
                value: newModifier.name,
                label: newModifier.name
            };

            await firestore().collection('modifiers').doc(id).set(modifierData);

            // Add to selected modifiers
            const newSelectedModifiers = isMulti
                ? [...selectedModifiers, id]
                : id;

            onChange && onChange(newSelectedModifiers);
            onCreateModifier && onCreateModifier(modifierData);

            setNewModifierModal(false);
            setNewModifier({ name: '', name_ar: '' });
        } catch (error) {
            console.error('Error creating modifier:', error);
        } finally {
            setLoading(false);
        }
    };

    // Get available options (filtered to exclude already selected)
    const getAvailableOptions = () => {
        const filteredSorted = filterSortedObject(sortedModifiers, selectedModifiers);
        return Object.values(filteredSorted);
    };

    // Get selected options for display
    const getSelectedOptions = () => {
        const allOptions = getAllOptions(sortedModifiers);
        const selected = allOptions.filter(option =>
            isMulti
                ? selectedModifiers.includes(option.key)
                : selectedModifiers === option.key
        );
        return isMulti ? selected : selected[0] || null;
    };

    // Remove selected modifier
    const removeModifier = (modifierKey) => {
        if (isMulti) {
            const newSelected = selectedModifiers.filter(key => key !== modifierKey);
            onChange && onChange(newSelected);
        } else {
            onChange && onChange(null);
        }
    };

    return (
        <div className="modifiers-select-wrapper">
            {/* React Select Component */}
            <Creatable
                formatGroupLabel={formatGroupLabel}
                components={{ Input: CustomSelectInput }}
                className={className}
                options={getAvailableOptions()}
                value={getSelectedOptions()}
                onChange={handleChange}
                onCreateOption={handleCreateOption}
                isDisabled={disabled}
                placeholder={placeholder}
                isMulti={isMulti}
                isClearable
            />

            {/* Selected Modifiers Display (for multi-select) */}
            {isMulti && selectedModifiers.length > 0 && (
                <div className="mb-2 mt-2" style={{
                    minHeight: 'auto',
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    flexWrap: 'wrap',
                }}>
                    {getAllOptions(sortedModifiers)
                        ?.filter(modifier => selectedModifiers.includes(modifier.key))
                        ?.map((modifier, index) => (
                            <div key={index} style={{
                                margin: '5px',
                                padding: '5px 10px',
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: '0.875rem'
                            }}>
                                <span>{modifier.label}</span>
                                <button
                                    type="button"
                                    onClick={() => removeModifier(modifier.key)}
                                    style={{
                                        marginLeft: '8px',
                                        background: 'none',
                                        border: 'none',
                                        color: '#6c757d',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        lineHeight: '1',
                                        padding: '0'
                                    }}
                                    title="Remove modifier"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                </div>
            )}

            {/* New Modifier Modal */}
            <Modal isOpen={newModifierModal} toggle={() => setNewModifierModal(false)}>
                <ModalHeader toggle={() => setNewModifierModal(false)}>
                    Add New Modifier
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label for="modifierName">Modifier Name</Label>
                        <Input
                            type="text"
                            id="modifierName"
                            value={newModifier.name}
                            onChange={(e) => setNewModifier({ ...newModifier, name: e.target.value })}
                            placeholder="Enter modifier name"
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label for="modifierNameAr">Arabic Name</Label>
                        <Input
                            type="text"
                            id="modifierNameAr"
                            value={newModifier.name_ar}
                            onChange={(e) => setNewModifier({ ...newModifier, name_ar: e.target.value })}
                            placeholder="Enter Arabic name"
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setNewModifierModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={saveNewModifier}
                        disabled={!newModifier.name || !newModifier.name_ar || loading}
                    >
                        {loading ? 'Adding...' : 'Add Modifier'}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default ModifiersSelect;
